import React, {useMemo, useRef} from 'react';
import {createPortal} from 'react-dom';
import FocusLock from 'react-focus-lock';
import {FormattedMessage} from 'react-intl';
import PropTypes from 'prop-types';

import {
  Box,
  Image,
  Text,
  useBreakpointValue,
  useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui';
import {Markup} from '@amblique/base/app/components/shared/markup';
import {DesktopPanelView} from '@amblique/king-living/app/components/configuration-selector/desktop-panel-view';
import {MobileDrawerView} from '@amblique/king-living/app/components/configuration-selector/mobile-drawer-view';
import ReviewSection from '@amblique/king-living/app/components/configuration-selector/review-section';
import {PIM_SQUARE_THUMBNAIL_PX, resolveProductThumbnailUrl} from '@amblique/king-living/app/components/configuration-selector/utils';
import {useComplexGalleryState, useConfigurationSelector} from '@amblique/king-living/app/hooks/use-configuration-selector';
import {useCustomizeJourney} from '@amblique/king-living/app/hooks/use-customize-journey';
import {
  resolveGalleryProduct,
  resolveGalleryVariationAttributes,
  resolveShowViewDetailsSection,
  shouldShowCommittedFlyoutGallery,
} from '@amblique/king-living/app/utils/gallery-state-utils';
import {
  buildLayerFromProduct,
  buildLayersFromSelections,
  resolveCommittedSelectionGalleryResource,
  resolveViewDetailsOptionGalleryResource,
} from '@amblique/king-living/app/utils/gallery-resource-utils';
import {PDP_DESKTOP_CONFIG_PANEL_WIDTH_PX} from '@amblique/king-living/app/constants/pdp-layout';
import {usePdpConfigPanelA11y} from '@amblique/king-living/app/hooks/use-pdp-config-panel-a11y';
import colors from '@amblique/king-living/app/theme/foundations/colors';
import space from '@amblique/king-living/app/theme/foundations/space';

const CHOOSE_LAYOUT_TRIGGER_MARGIN_TOP = {base: 10, lg: 12};

/** Separator below "Choose your layout" block — mobile vs desktop per design keyline */
const CHOOSE_LAYOUT_TRIGGER_CONTAINER_DIVIDER = {
  paddingBottom: {base: space[14], lg: space[16]},
  borderBottomWidth: '1px',
  borderBottomStyle: 'solid',
  borderBottomColor: colors.greyscale90Alpha[10],
  marginBottom: {base: space[4], lg: space[6]},
};

/**
 * ConfigurationSelector — must be rendered inside a CustomizeJourneyProvider.
 *
 * Single instance, no isDesktopPanel prop.
 * Internal breakpoint switch determines which layout to render:
 *   - Desktop (isMobile=false): DesktopShell (ReviewSection + DesktopPanelView as a Flex row)
 *     - When isOpen=false: renders trigger card only (for desktop panel-closed state)
 *     - When isOpen=true:  renders full DesktopShell
 *   - Mobile (isMobile=true): trigger card + MobileDrawerView + portal
 *
 * SSR hydration guard: isMobile = isMobileBreakpoint === true
 *   undefined (SSR) → false (desktop) — matches the old isDesktopPanel=false default on SSR.
 */
const ConfigurationSelector = ({
  product,
  configProducts = [],
  coverGroups = [],
  isLoading = false,
  isOpen,
  selectedId,
  headerTitle,
  onOpen,
  onClose,
  onSelect,
  panelMode = 'choose-layout',
  variationParams,

  /** Rendered below the layout trigger card, above the section keyline (e.g. PDP promotion accordion). */
  chooseLayoutFooter = null,

  /** Simple PDP finished good — section title "Layout", hide Change (TSD §7). */
  isFinishedGoodSimplePdp = false,
}) => {
  const styles = useMultiStyleConfig('ConfigurationSelector', {});
  const {flyoutDraftState, committedSelection} = useCustomizeJourney();

  // SSR guard: undefined → false (desktop), avoids hydration mismatch
  const isMobileBreakpoint = useBreakpointValue({base: true, lg: false});
  const isMobile = isMobileBreakpoint === true;

  const {
    closeViewDetails,
    coverVariantDetailsProduct,
    customizeGalleryVariationValuesForPanel,
    drawerViewportRect,
    effectiveSelectedId,
    hasMultipleConfigs,
    openViewDetails,
    panelHeaderInfo,
    selectedProduct,
    viewDetailsProduct,
    viewDetailsShelvesUi,
    handleClose,
    handleCustomizeGalleryProductChange,
    handleCustomizeSlotPreview,
    handleDoneClick,
    setPanelHeaderInfo,
    setDrawerEscHandler,
  } = useConfigurationSelector({
    configProducts,
    isOpen,
    onClose,
    onOpen,
    panelMode,
    product,
    selectedId,
  });

  const isCoverPickerOpen =
    panelHeaderInfo?.customizeStepKind === 'cover' && Boolean(panelHeaderInfo?.onBack);

  const complexGalleryState = useComplexGalleryState({
    committedSelections: committedSelection?.selections,
    draftSelections: flyoutDraftState?.draftSelections,
    galleryPreviewProduct: flyoutDraftState?.galleryPreview?.product,
    isCoverPickerOpen,
    masterProduct: product,
    panelMode,
  });

  const galleryProduct = useMemo(
    () => resolveGalleryProduct({
      configPanelMode: panelMode,
      galleryPreviewProduct: flyoutDraftState?.galleryPreview?.product,
      selectedConfigProduct: selectedProduct,
      product,
    }),
    [flyoutDraftState?.galleryPreview?.product, panelMode, product, selectedProduct]
  );

  const galleryVariationAttributes = useMemo(
    () => (panelMode === 'customize' && complexGalleryState ?
      complexGalleryState.galleryVariationAttributes :
      resolveGalleryVariationAttributes({
        configPanelMode: panelMode,
        flyoutDraftSelections: flyoutDraftState?.draftSelections,
        committedSelections: committedSelection?.selections,
        galleryPreviewVariationValues: flyoutDraftState?.galleryPreview?.variationValues,
        variationParams,
      })),
    [
      complexGalleryState,
      panelMode,
      flyoutDraftState?.draftSelections,
      committedSelection?.selections,
      flyoutDraftState?.galleryPreview?.variationValues,
      variationParams
    ]
  );

  const masterProductId = product?.id ?? product?.productId;

  const showViewDetailsSection = useMemo(
    () => resolveShowViewDetailsSection({
      baselineVariationValues: committedSelection?.selections?.cover?.variationValues,
      configPanelMode: panelMode,
      effectiveSelectedId,
      hasCustomizeInteraction: Boolean(flyoutDraftState?.hasUserInteracted),
      masterProductId,
      previewVariationValues: flyoutDraftState?.galleryPreview?.variationValues,
      viewDetailsProduct: coverVariantDetailsProduct,
    }),
    [
      panelMode,
      effectiveSelectedId,
      committedSelection?.selections?.cover?.variationValues,
      flyoutDraftState?.hasUserInteracted,
      flyoutDraftState?.galleryPreview?.variationValues,
      masterProductId,
      coverVariantDetailsProduct
    ]
  );

  const desktopGalleryLayers = useMemo(() => {
    if (panelMode === 'customize') return complexGalleryState?.galleryLayers ?? [];

    return buildLayersFromSelections(
      flyoutDraftState?.draftSelections ?? committedSelection?.selections
    );
  }, [
    complexGalleryState,
    panelMode,
    flyoutDraftState?.draftSelections,
    committedSelection?.selections
  ]);

  const showCommittedFlyoutGallery = useMemo(
    () => shouldShowCommittedFlyoutGallery({
      committedMode: committedSelection?.mode,
      committedSelections: committedSelection?.selections,
      configPanelMode: panelMode,
      flyoutIsOpen: flyoutDraftState?.isOpen,
      hasUserInteracted: flyoutDraftState?.hasUserInteracted,
    }),
    [
      panelMode,
      flyoutDraftState?.isOpen,
      flyoutDraftState?.hasUserInteracted,
      committedSelection?.selections,
      committedSelection?.mode
    ]
  );

  const desktopCommittedGalleryResourceOverride = useMemo(() => {
    if (!showCommittedFlyoutGallery) return null;

    return resolveCommittedSelectionGalleryResource(
      product,
      committedSelection.selections,
      {allowCylindo: true}
    );
  }, [showCommittedFlyoutGallery, product, committedSelection?.selections]);

  // View Details overlay — gallery must follow the clicked option, not the selected
  // draft slot (desktopGalleryLayers includes the committed selection's cylindo/video).
  const viewDetailsGalleryResourceOverride = useMemo(() => {
    if (!viewDetailsProduct || panelMode !== 'customize') return null;

    return resolveViewDetailsOptionGalleryResource(
      viewDetailsProduct,
      product,
      customizeGalleryVariationValuesForPanel
    );
  }, [
    customizeGalleryVariationValuesForPanel,
    panelMode,
    product,
    viewDetailsProduct
  ]);

  // View Details overlay layers: option layers + the viewed product as last layer.
  // Used on desktop inline ReviewSection when no override is set.
  const viewDetailsOverlayLayers = useMemo(() => {
    if (!viewDetailsProduct) return desktopGalleryLayers;

    return [...desktopGalleryLayers, buildLayerFromProduct(viewDetailsProduct)];
  }, [desktopGalleryLayers, viewDetailsProduct]);

  // ─── Desktop path ─────────────────────────────────────────────────────────────
  const hasConfigData = (configProducts && configProducts.length > 0);
  const layoutSectionTitle = isFinishedGoodSimplePdp ?
    (
      <FormattedMessage
        defaultMessage="Layout"
        id="configuration_selector.section.title.layout"
      />
    ) :
    (
      <FormattedMessage
        defaultMessage="Choose your layout"
        id="configuration_selector.section.title"
      />
    );
  const showChangeLayoutControl = hasMultipleConfigs && !isFinishedGoodSimplePdp;

  const desktopPanelInitialFocusRef = useRef(null);

  const {panelRef: desktopPanelRef} = usePdpConfigPanelA11y({
    isActive: Boolean(isOpen && !isMobile),
    manageInitialFocus: false,
    onDismiss: handleClose,
  });

  if (!isMobile) {
    // Desktop, panel closed: show trigger card so user can open the panel.
    // ProductView gates this element with !showDesktopConfigPanel, so isOpen here is always false.
    if (!isOpen) {
      if (!hasConfigData && !isLoading) return null;

      const imageUrl = resolveProductThumbnailUrl(product);
      const title = product?.name ?? '';

      return (
        <Box
          className="choose-your-layout-trigger-container"
          marginTop={CHOOSE_LAYOUT_TRIGGER_MARGIN_TOP}
          {...CHOOSE_LAYOUT_TRIGGER_CONTAINER_DIVIDER}
        >
          <Text {...styles.sectionHeaderChooseYourLayout}>
            {layoutSectionTitle}
          </Text>
          <Box
            className="choose-your-layout-trigger"
            data-testid="choose-your-layout-trigger"
            {...styles.triggerCard}
            cursor={showChangeLayoutControl ? 'pointer' : 'default'}
            role={showChangeLayoutControl ? 'button' : undefined}
            tabIndex={showChangeLayoutControl ? 0 : undefined}
            onClick={showChangeLayoutControl ? () => onOpen?.() : undefined}
            onKeyDown={showChangeLayoutControl ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onOpen?.();
              }
            } : undefined}
            {...(!showChangeLayoutControl && {_hover: {}, _focus: {}})}
          >
            {imageUrl ? (
              <Box {...styles.triggerImageWrapper}>
                <Image
                  alt={title}
                  htmlHeight={PIM_SQUARE_THUMBNAIL_PX}
                  htmlWidth={PIM_SQUARE_THUMBNAIL_PX}
                  src={imageUrl}
                  {...styles.triggerImage}
                />
              </Box>
            ) : null}
            <Box {...styles.triggerContent}>
              <Text {...styles.triggerTitle}>{title}</Text>
              {product?.shortDescription && (
                <Markup source={product.shortDescription} {...styles.triggerDescription} />
              )}
            </Box>
            {showChangeLayoutControl && (
              <Text {...styles.triggerChangeLabel}>
                <FormattedMessage
                  defaultMessage="Change"
                  id="configuration_selector.button.change"
                />
              </Text>
            )}
          </Box>
          {chooseLayoutFooter ?
            <Box marginTop={{base: 3, lg: 4}}>{chooseLayoutFooter}</Box> :
            null}
        </Box>
      );
    }

    // Desktop, panel open: ReviewSection stays in the left gallery column (inline);
    // DesktopPanelView is portalled to document.body as a fixed overlay on the right.
    //
    // `key` (choose-layout only): forces a fresh ImageGalleryV2 instance every time
    // the previewed master changes. Without this, internal gallery state
    // (cylindoError, cylindoViewerReady, pimFirstSlideReady) can persist from a
    // previously-previewed sibling that lacked images, causing the next preview to
    // render the "Image not available" placeholder even when it has valid Cylindo
    // data. customize mode keeps the same key so the LHS gallery stays stable as
    // the user picks options inside a single master.
    return (
      <>
        <ReviewSection
          key={
            panelMode === 'choose-layout' ?
              (galleryProduct?.id ?? galleryProduct?.productId ?? 'choose-layout-gallery') :
              'customize-gallery'
          }
          desktopLightboxRightInsetPx={PDP_DESKTOP_CONFIG_PANEL_WIDTH_PX}
          forceCylindoCheck={panelMode === 'customize'}
          isOpen={isOpen}
          isThumbnailZoomEnabled={panelMode !== 'choose-layout'}
          layers={panelMode === 'customize' ? desktopGalleryLayers : undefined}
          masterProduct={panelMode === 'customize' ? product : galleryProduct}
          previewProduct={null}
          selectedVariationAttributes={galleryVariationAttributes}
          selections={committedSelection?.selections}
          showMobileConfigThumbnails={panelMode === 'choose-layout'}
          showViewDetailsSection={showViewDetailsSection}
          viewDetailsProduct={coverVariantDetailsProduct}
          galleryResourceOverride={
            viewDetailsGalleryResourceOverride ?? desktopCommittedGalleryResourceOverride
          }
          imageZoomFullScreen
        />
        {typeof document !== 'undefined' && createPortal(
          <FocusLock initialFocusRef={desktopPanelInitialFocusRef} returnFocus>
            <Box
              ref={desktopPanelRef}
              aria-modal="true"
              bg="white"
              bottom={0}
              className="configuration-selector__desktop-panel"
              display={{base: 'none', lg: 'flex'}}
              flexDirection="column"
              overflow="hidden"
              position="fixed"
              right={0}
              role="dialog"
              top={0}
              width={`${PDP_DESKTOP_CONFIG_PANEL_WIDTH_PX}px`}
              zIndex={1100}
            >
              <DesktopPanelView
                complexGalleryState={complexGalleryState}
                configProducts={configProducts}
                desktopPanelInitialFocusRef={desktopPanelInitialFocusRef}
                effectiveSelectedId={effectiveSelectedId}
                headerTitle={headerTitle}
                isLoading={isLoading}
                panelHeaderInfo={panelHeaderInfo}
                panelMode={panelMode}
                product={product}
                selectedProduct={selectedProduct}
                setDrawerEscHandler={setDrawerEscHandler}
                onClose={handleClose}
                onDone={handleDoneClick}
                onGalleryProductChange={handleCustomizeGalleryProductChange}
                onHeaderChange={setPanelHeaderInfo}
                onSelect={onSelect}
                onSlotProductPreview={handleCustomizeSlotPreview}
                onViewDetails={openViewDetails}
              />
            </Box>
          </FocusLock>,
          document.body
        )}
      </>
    );
  }

  // ─── Mobile path (trigger card + drawer + portals) ────────────────────────────

  if (!hasConfigData && !isLoading && panelMode !== 'customize') return null;

  const imageUrl = resolveProductThumbnailUrl(product);
  const title = product?.name ?? '';

  return (
    <Box className="configuration-selector-inner-container">
      {hasConfigData && (
        <Box
          className="choose-your-layout-trigger-container"
          marginTop={CHOOSE_LAYOUT_TRIGGER_MARGIN_TOP}
          {...CHOOSE_LAYOUT_TRIGGER_CONTAINER_DIVIDER}
        >
          <Text {...styles.sectionHeaderChooseYourLayout}>
            {layoutSectionTitle}
          </Text>
          <Box
            {...styles.triggerCard}
            className="choose-your-layout-trigger"
            cursor={showChangeLayoutControl ? 'pointer' : 'default'}
            data-testid={showChangeLayoutControl ? 'choose-your-layout-btn' : 'choose-your-layout-trigger'}
            role={showChangeLayoutControl ? 'button' : undefined}
            tabIndex={showChangeLayoutControl ? 0 : undefined}
            onClick={showChangeLayoutControl ? () => onOpen?.() : undefined}
            onKeyDown={showChangeLayoutControl ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault(); onOpen?.();
              }
            } : undefined}
            {...(!showChangeLayoutControl && {_hover: {}, _focus: {}})}
          >
            {imageUrl ? (
              <Box {...styles.triggerImageWrapper}>
                <Image
                  alt={title}
                  htmlHeight={PIM_SQUARE_THUMBNAIL_PX}
                  htmlWidth={PIM_SQUARE_THUMBNAIL_PX}
                  src={imageUrl}
                  {...styles.triggerImage}
                />
              </Box>
            ) : null}
            <Box {...styles.triggerContent}>
              <Text {...styles.triggerTitle}>{title}</Text>
              {product?.shortDescription && (
                <Markup source={product.shortDescription} {...styles.triggerDescription} />
              )}
            </Box>
            {showChangeLayoutControl && (
              <Text {...styles.triggerChangeLabel}>
                <FormattedMessage
                  defaultMessage="Change"
                  id="configuration_selector.button.change"
                />
              </Text>
            )}
          </Box>
          {chooseLayoutFooter ?
            <Box marginTop={{base: 3, lg: 4}}>{chooseLayoutFooter}</Box> :
            null}
        </Box>
      )}

      <MobileDrawerView
        complexGalleryState={complexGalleryState}
        configProducts={configProducts}
        coverVariantDetailsProduct={coverVariantDetailsProduct}
        drawerViewportRect={drawerViewportRect}
        effectiveSelectedId={effectiveSelectedId}
        headerTitle={headerTitle}
        isLoading={isLoading}
        isOpen={panelMode === 'choose-layout' ? (isOpen && hasMultipleConfigs) : isOpen}
        panelHeaderInfo={panelHeaderInfo}
        panelMode={panelMode}
        product={product}
        selectedProduct={selectedProduct}
        setDrawerEscHandler={setDrawerEscHandler}
        onClose={handleClose}
        onDone={handleDoneClick}
        onGalleryProductChange={handleCustomizeGalleryProductChange}
        onHeaderChange={setPanelHeaderInfo}
        onSelect={onSelect}
        onSlotProductPreview={handleCustomizeSlotPreview}
        onViewDetails={openViewDetails}
      />

      {/* Unified "View Details" overlay — adapts to panelMode */}
      <ReviewSection
        key={viewDetailsProduct?.id ?? viewDetailsProduct?.productId ?? 'view-details-overlay'}
        forceCylindoCheck={panelMode === 'customize'}
        galleryResourceOverride={viewDetailsGalleryResourceOverride}
        isOpen={viewDetailsProduct !== null}
        isThumbnailZoomEnabled={panelMode !== 'choose-layout'}
        masterProduct={panelMode === 'customize' ? product : viewDetailsProduct}
        overlayDynamicTitleFromShortDescription={viewDetailsShelvesUi}
        previewProduct={panelMode === 'customize' ? viewDetailsProduct : null}
        selectedVariationAttributes={panelMode === 'customize' ? customizeGalleryVariationValuesForPanel : undefined}
        selections={committedSelection?.selections}
        showMobileConfigThumbnails={panelMode === 'choose-layout'}
        showViewDetailsSection={effectiveSelectedId != null}
        layers={
          panelMode === 'customize' && !viewDetailsGalleryResourceOverride ?
            viewDetailsOverlayLayers :
            undefined
        }
        asOverlay
        imageZoomFullScreen
        onClose={closeViewDetails}
      />
    </Box>
  );
};

ConfigurationSelector.displayName = 'ConfigurationSelector';

ConfigurationSelector.propTypes = {
  product: PropTypes.object.isRequired,
  configProducts: PropTypes.array,
  coverGroups: PropTypes.array,
  isLoading: PropTypes.bool,
  isOpen: PropTypes.bool,
  selectedId: PropTypes.string,
  headerTitle: PropTypes.string,
  onOpen: PropTypes.func,
  onClose: PropTypes.func,
  onSelect: PropTypes.func,
  panelMode: PropTypes.oneOf(['choose-layout', 'customize']),
  variationParams: PropTypes.object,
  chooseLayoutFooter: PropTypes.node,
  isFinishedGoodSimplePdp: PropTypes.bool,
};

export default ConfigurationSelector;
